import React from 'react';
import './ItemCard.scss';

/**
 * ItemCard - A local implementation inspired by Cloudscape's ItemCard component.
 * Provides a card layout with header, description, icon, actions, body (children), and footer sections.
 *
 * Props:
 * - header: React.ReactNode - Heading element of the card
 * - description: React.ReactNode - Subtitle displayed below the header
 * - icon: React.ReactNode - Icon displayed next to the header
 * - actions: React.ReactNode - Actions (buttons/links) in the header area
 * - children: React.ReactNode - Main content of the card
 * - footer: React.ReactNode - Footer content at the bottom
 * - variant: 'default' | 'embedded' - Visual variant (default: 'default')
 * - highlighted: boolean - Whether the card has a highlighted border
 * - fullHeight: boolean - Whether the card stretches to full height of its container
 * - disableHeaderPaddings: boolean - Remove default padding from header
 * - disableContentPaddings: boolean - Remove default padding from content area
 * - disableFooterPaddings: boolean - Remove default padding from footer
 * - onClick: function - Click handler for the card
 * - className: string - Additional CSS class names
 * - style: object - Inline styles for the root element
 */
const ItemCard = ({
  header,
  description,
  icon,
  actions,
  children,
  footer,
  variant = 'default',
  highlighted = false,
  fullHeight = false,
  disableHeaderPaddings = false,
  disableContentPaddings = false,
  disableFooterPaddings = false,
  onClick,
  className = '',
  style,
  ...restProps
}) => {
  const rootClasses = [
    'item-card',
    `item-card--variant-${variant}`,
    highlighted && 'item-card--highlighted',
    fullHeight && 'item-card--full-height',
    onClick && 'item-card--clickable',
    className,
  ].filter(Boolean).join(' ');

  const headerRowEmpty = !header && !description && !icon && !actions;

  return (
    <div
      className={rootClasses}
      style={style}
      onClick={onClick}
      {...restProps}
    >
      <div className="item-card__inner">
        {!headerRowEmpty && (
          <div
            className={[
              'item-card__header',
              disableHeaderPaddings && 'item-card__header--no-padding',
              !actions && 'item-card__header--without-actions',
            ].filter(Boolean).join(' ')}
          >
            <div className="item-card__header-main">
              {icon && <div className="item-card__icon">{icon}</div>}
              <div className="item-card__header-content">
                {header && <div className="item-card__header-inner">{header}</div>}
                {description && <div className="item-card__description">{description}</div>}
              </div>
            </div>
            {actions && <div className="item-card__actions">{actions}</div>}
          </div>
        )}

        {children && (
          <div
            className={[
              'item-card__body',
              disableContentPaddings && 'item-card__body--no-padding',
            ].filter(Boolean).join(' ')}
          >
            {children}
          </div>
        )}

        {footer && (
          <div
            className={[
              'item-card__footer',
              disableFooterPaddings && 'item-card__footer--no-padding',
            ].filter(Boolean).join(' ')}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

export default ItemCard;
